import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Plus, Trash2, UserPlus, X, Users as UsersIcon } from 'lucide-react';
import { orgs, users } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { MetricCard } from '../components/MetricCard';
import type { Organisation } from '../types';

// ── Shared styles ──────────────────────────────────────────────────

const inputCls =
  'w-full bg-surface-2 border border-surface-3 rounded-md px-2.5 py-1.5 text-xs text-gray-200 placeholder-gray-600 focus:outline-none focus:border-blue-500';
const selectCls =
  'w-full bg-surface-2 border border-surface-3 rounded-md px-2.5 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-blue-500';
const btnPrimary =
  'px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

const TIERS = ['base', 'pro', 'enterprise'] as const;

// ── Add Client form ────────────────────────────────────────────────

function AddClientForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [orgName, setOrgName] = useState('');
  const [tier, setTier] = useState<string>('base');

  const mutation = useMutation({
    mutationFn: () =>
      orgs.create({ org_name: orgName.trim(), org_type: 'client', tier }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgs'] });
      onClose();
    },
  });

  return (
    <form
      className="card mb-4"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-gray-300">New Client Organisation</span>
        <button type="button" onClick={onClose} className="text-gray-600 hover:text-gray-300">
          <X size={14} />
        </button>
      </div>
      <div className="flex gap-3 items-end">
        <div className="flex-1">
          <label className="block text-[10px] text-gray-500 mb-1">Organisation Name</label>
          <input
            className={inputCls}
            placeholder="Client company name"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            required
          />
        </div>
        <div className="w-32">
          <label className="block text-[10px] text-gray-500 mb-1">Tier</label>
          <select className={selectCls} value={tier} onChange={(e) => setTier(e.target.value)}>
            {TIERS.map((t) => (
              <option key={t} value={t}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </option>
            ))}
          </select>
        </div>
        <button type="submit" className={btnPrimary} disabled={mutation.isPending}>
          {mutation.isPending ? 'Creating...' : 'Add Client'}
        </button>
      </div>
      {mutation.isError && (
        <p className="mt-2 text-[10px] text-red-400">{(mutation.error as Error).message}</p>
      )}
    </form>
  );
}

// ── Invite Admin form ──────────────────────────────────────────────

function InviteAdminForm({
  client,
  onClose,
}: {
  client: Organisation;
  onClose: () => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      users.invite(email.trim(), 'admin', {
        name: name.trim() || undefined,
        org_id: client.org_id,
        org_type: client.org_type,
      }),
    onSuccess: () => onClose(),
  });

  return (
    <form
      className="mt-2 p-3 bg-surface-3/40 rounded-lg border border-surface-3 space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
          Invite Admin — {client.org_name}
        </span>
        <button type="button" onClick={onClose} className="text-gray-600 hover:text-gray-300">
          <X size={13} />
        </button>
      </div>
      <input
        className={inputCls}
        type="email"
        placeholder="Email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <input
        className={inputCls}
        placeholder="Full name (optional)"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      {mutation.isError && (
        <p className="text-[10px] text-red-400">{(mutation.error as Error).message}</p>
      )}
      {mutation.isSuccess && <p className="text-[10px] text-green-400">Invite sent.</p>}
      <div className="flex justify-end">
        <button type="submit" className={btnPrimary} disabled={mutation.isPending}>
          {mutation.isPending ? 'Sending...' : 'Send Invite'}
        </button>
      </div>
    </form>
  );
}

// ── Client card ────────────────────────────────────────────────────

function ClientCard({ client }: { client: Organisation }) {
  const [showInvite, setShowInvite] = useState(false);
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: () => orgs.delete(client.org_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgs'] });
    },
  });

  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-emerald-500/15 rounded-lg flex items-center justify-center">
            <Building2 size={16} className="text-emerald-400" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-white">{client.org_name}</h3>
            <div className="text-[10px] text-gray-500 capitalize">
              {client.tier} tier
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-sm font-mono text-gray-300">{client.device_serials.length}</div>
            <div className="text-[10px] text-gray-500">devices</div>
          </div>
          <button
            type="button"
            onClick={() => setShowInvite((v) => !v)}
            className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-blue-400 transition-colors"
          >
            <UserPlus size={12} />
            <span>Invite Admin</span>
          </button>
          <button
            type="button"
            onClick={() => {
              if (confirm(`Delete ${client.org_name}?`)) deleteMutation.mutate();
            }}
            className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-red-400 transition-colors"
            disabled={deleteMutation.isPending}
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>
      {deleteMutation.isError && (
        <p className="mt-2 text-[10px] text-red-400">{(deleteMutation.error as Error).message}</p>
      )}

      {/* Device serials list */}
      {client.device_serials.length > 0 && (
        <div className="mt-3 pt-3 border-t border-surface-3">
          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
            Commissioned Devices
          </div>
          <div className="flex flex-wrap gap-1.5">
            {client.device_serials.map((serial) => (
              <span
                key={serial}
                className="px-2 py-0.5 bg-surface-2 rounded text-[10px] font-mono text-kinetica-400"
              >
                {serial}
              </span>
            ))}
          </div>
        </div>
      )}

      {showInvite && (
        <InviteAdminForm client={client} onClose={() => setShowInvite(false)} />
      )}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────

export default function MyClientsPage() {
  const { user } = useAuth();
  const [showAddClient, setShowAddClient] = useState(false);

  const { data: organisations = [], isLoading } = useQuery({
    queryKey: ['orgs'],
    queryFn: orgs.list,
  });

  const clients = organisations.filter(
    (o: Organisation) => o.org_type === 'client' && o.parent_org_id === user?.org_id,
  );
  const totalDevices = clients.reduce(
    (sum: number, o: Organisation) => sum + o.device_serials.length,
    0,
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">My Clients</h2>
        <button
          type="button"
          onClick={() => setShowAddClient((v) => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-md transition-colors"
        >
          <Plus size={13} />
          Add Client
        </button>
      </div>

      {showAddClient && <AddClientForm onClose={() => setShowAddClient(false)} />}

      {/* Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <MetricCard label="Clients" value={clients.length} />
        <MetricCard label="Total Devices" value={totalDevices} accent="text-kinetica-400" />
        <MetricCard
          label="Avg Devices / Client"
          value={clients.length > 0 ? (totalDevices / clients.length).toFixed(1) : '0'}
        />
      </div>

      {/* Client list */}
      <div className="space-y-3">
        {isLoading ? (
          <div className="card text-center text-gray-500 text-sm py-8">Loading clients...</div>
        ) : clients.length === 0 ? (
          <div className="card text-center py-8">
            <UsersIcon className="mx-auto mb-3 text-gray-600" size={32} />
            <p className="text-sm text-gray-400">No client organisations yet</p>
            <p className="text-xs text-gray-600 mt-1">
              Add your first client to start commissioning devices
            </p>
          </div>
        ) : (
          clients.map((client: Organisation) => (
            <ClientCard key={client.org_id} client={client} />
          ))
        )}
      </div>
    </div>
  );
}
